import { BreadcrumbComponent, Button, CIPageHeader, Dropdown, Input, MyIcon, Textarea } from 'ccs-ui-components';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Toast } from 'react-bootstrap';
import ToastContainer from 'react-bootstrap/ToastContainer';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import useLoader from '../../../common/hooks/Loader';
import { groupOptions } from '../../constants/GroupNameDropdown';
import { regex } from '../../constants/JdRegex';
import { journeyBuilderRoutes } from '../../constants/JdRoutes';
import { resolveJourneyFormBackPath } from '../../utilities/JourneyNavigationUtils';
import { createJourney, getAllJourneys, getJourney } from '../../store/_api';
import { createJourneyFormSchema } from '../../store/_api/SchemaDefs';
import { DataLoadingState, JourneyValidation, SetDebounce } from './TypeDefs';

import classes from './CreateJourney.module.scss';
import { ViewJourneyData } from '../../store/_api/TypeDefs';

const CreateJourney = () => {
  // state variables
  const [journeyName, setJourneyName] = useState<string>('');
  const [journeyDescription, setJourneyDescription] = useState<string>('');
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [invalidJourneyName, setInvalidJourneyName] = useState<string>(JourneyValidation.VALID);
  const [isJourneyNameInvalid, setIsJourneyNameInvalid] = useState<boolean>(false);
  const [clonedJourneyDialog, setClonedJourneyDialog] = useState<boolean>(false);
  const [resp, setApiResp] = useState<ViewJourneyData>();
  const [apiResponseStatus, setApiResponseStatus] = useState<DataLoadingState>(
    DataLoadingState.COMPLETED
  );
  const [selectGroup, setSelectGroup] = useState<string>('');
  const location = useLocation();
  const { cloneJourneyName, cloneJourneyVersion } = location.state || '';
  const callDebounceRef = useRef(SetDebounce.NOT_SET);

  // hooks
  const router = useNavigate();
  const { t: translate } = useTranslation();
  const loader = useLoader();

  // local variables
  const toastDelayTime = 4000;
  const journeyNameMaxLength = 40;
  const debounceDelay = 2000;
  const cloneLabel = 'Clone';

  /**
   * @method debounceHandler
   * @description handles delay after the journey name input
   */
  const debounceHandler = useCallback(
    _.debounce((value: string) => {
      if (callDebounceRef.current === SetDebounce.NOT_SET) {
        journeyNameValidation(value);
      }
    }, debounceDelay),
    []
  );

  /**
   * @method nameChangeHandler
   * @description validation for journey name
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const nameChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length === 0) {
      setInvalidJourneyName(JourneyValidation.EMPTY);
    } else if (e.target.value.length > journeyNameMaxLength) {
      setInvalidJourneyName(JourneyValidation.LENGTH_EXCEEDED);
      return;
    } else if (regex.name.test(e.target.value)) {
      setInvalidJourneyName(JourneyValidation.VALID);
      debounceHandler(e.target.value);
      callDebounceRef.current = SetDebounce.NOT_SET;
    } else {
      setInvalidJourneyName(JourneyValidation.INVALID);
    }
    setJourneyName(e.target.value);
  };

  /**
   * @method selectGroupHandler
   * @description storing|setting the selected group
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const selectGroupHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectGroup(e.target.value);
  };

    // To toggle cloned journey dialog pop-up
  const closeClonedJourneyDialog = () => {
    setClonedJourneyDialog(false);
  };

  /**
   * @method descriptionChangeHandler
   * @description storing|setting the entered description
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const descriptionChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setJourneyDescription(e.target.value);
  };

  /**
   * @method modalCloseHandler
   * @description closing the toast modal
   */
  const modalCloseHandler = () => {
    setShowModal(false);
  };

  const handleCancelCreateJourney = () => {
    router(resolveJourneyFormBackPath(location.state));
  };

  /**
    * @method formValid
    * @description validation function to validate the Journey Name and Description of the form fields using joi.
    */
  const formValid = createJourneyFormSchema.validate(
    {
      journeyName,
      journeyDescription
    },
    { abortEarly: false }
  );

  /**
   * @method getCloneJourneyData
   * @description function to fetch journey data from getJourney api and set value for group name and discription
   * @param cloneJourneyName
   * @param cloneJourneyVersion
   */
  const getCloneJourneyData = async (cloneJourneyName: string, cloneJourneyVersion: number) => {
    const resp = await getJourney(cloneJourneyName, cloneJourneyVersion);
    if (resp) {
      setJourneyDescription(resp.journeyDescription);
      setSelectGroup(resp.journeyCategory);
      setApiResp(resp);
    }
  };

  /**
   * @method submitHandler
   * @description submitting the entered form details
   * @param {React.FormEvent<HTMLFormElement>} e
   */
  const submitHandler = async () => {
    loader.show();
    if (!await journeyNameValidation(journeyName)) {
      loader.hide();
      return;
    }

    if (!(invalidJourneyName !== JourneyValidation.VALID || journeyName.trim() === '' || apiResponseStatus === DataLoadingState.LOADING || isJourneyNameInvalid || callDebounceRef.current !== SetDebounce.NOT_SET)) {
      if (formValid.error) {
        setModalErrorMsg(formValid.error.message);
        setShowModal(true);
      } else if (resp && cloneJourneyName && cloneJourneyVersion) {
        const patchedJourneyJsonDetails = {
          ...resp.journeyJsonDetails,
          stepNodes: resp.journeyJsonDetails.stepNodes.map((node) => ({
            ...node,
            stepAction: 'create',
          })),
        };
        const completeJourneyPayload = {
          journeyName,
          journeyDescription,
          journeyType: '',
          version: 1,
          journeyCategory: selectGroup,
          journeyJsonDetails: patchedJourneyJsonDetails,
          stepDetails: resp.stepDetails
        };
        const response = await createJourney(completeJourneyPayload, cloneJourneyVersion);
        // navigate to canvas page on api success
        if (response) {
        router(journeyBuilderRoutes.journeyCanvas.replace(':id', journeyName).replace(':mode', 'edit'), { replace: true, state: { cloneJourneyName: journeyName, cloneJourneyVersion: 1 } });
        }
      } else {
        // navigate to canvas page on api success
        router(journeyBuilderRoutes.journeyCanvas.replace(':id', journeyName).replace(':mode', 'create').replace(':version', '1').concat(`?description=${journeyDescription}&group=${selectGroup}`), { replace: true, state: { cloneJourneyName, cloneJourneyVersion } });
      }
    }
    callDebounceRef.current = SetDebounce.SET;
    loader.hide();
  };

  /**
   * @method journeyNameValidation
   * @description validates the journey name from the list api response to check for the uniqueness.
   * @param name
   */
  const journeyNameValidation = async (name: string) => {
    if (name) {
      setApiResponseStatus(DataLoadingState.LOADING);
      const resp = await getAllJourneys({
        page: 0,
        pageSize: 10,
        validFromDate: '',
        validToDate: '',
        searchJourney: name,
        statusFilter: [],
        sortParameter: '',
        validationName: true
      });
      if (!resp.isError && (resp.statusCode === 200 || resp.statusCode === 204)) {
        setIsJourneyNameInvalid(resp.journeys.length > 0);
        setApiResponseStatus(DataLoadingState.COMPLETED);
        setInvalidJourneyName(JourneyValidation.VALID);
        callDebounceRef.current = SetDebounce.NOT_SET;
        return resp.journeys.length === 0;
      }
      setApiResponseStatus(DataLoadingState.FAILED);
      setInvalidJourneyName(JourneyValidation.INVALID);
      callDebounceRef.current = SetDebounce.NOT_SET;
    }
    return false;
  };

  /**
   * @method errorText
   * @description function to return error text string
   * @returns string
   */
  const errorText = () => {
    if (apiResponseStatus === DataLoadingState.FAILED) {
      return (translate('create_journey_component.network_failed'));
    }
    if (invalidJourneyName === JourneyValidation.EMPTY) {
      return (translate('create_journey_component.journey_name_required'));
    }
    if (invalidJourneyName === JourneyValidation.INVALID) {
      return (translate('create_journey_component.journey_name_invalid'));
    }
    if (invalidJourneyName === JourneyValidation.LENGTH_EXCEEDED) {
      return (translate('create_journey_component.journey_name_length_exceeded'));
    }
    if (isJourneyNameInvalid && invalidJourneyName === JourneyValidation.VALID) {
      return (translate('create_journey_component.journey_name_already_exists'));
    }
    return ('');
  };

  useEffect(() => {
    const app = document.querySelector('.app');
    if (app) {
      app.className = 'app no-padding';
    }
    return (() => {
      if (app) {
        app.className = 'app';
      }
    });
  }, []);

  useEffect(() => {
    if (cloneJourneyName && cloneJourneyVersion) {
      setJourneyName(`${cloneLabel}-${cloneJourneyName}`);
      getCloneJourneyData(cloneJourneyName, cloneJourneyVersion);
      setClonedJourneyDialog(true);
    }
  }, [cloneJourneyName]);

  return (
    <>
      {loader.render()}
      <div className={classes.toast}>
        <ToastContainer position="top-end" className="p-3">
          <Toast show={showModal} onClose={modalCloseHandler} bg="danger" autohide delay={toastDelayTime}>
            <Toast.Header>
              <strong className="me-auto" data-testid="toast-message">
                {translate('create_journey_component.title_toast_header')}
              </strong>
            </Toast.Header>
            <div className={classes['toast-body-class']}>
              <Toast.Body data-testid="toastBody">{modalErrorMsg}</Toast.Body>
            </div>
          </Toast>
        </ToastContainer>
      </div>
      <div className={classes['main-container']}>
        <div data-testid="createJourney">
          <div className={classes['create-journey']}>
            <div className={classes.breadcrumb}>
              <BreadcrumbComponent />
            </div>
            <div className={classes['form-div']}>
              <form data-testid="newJourney" autoComplete="off">
                <div className={classes['form-title']}>
                  <CIPageHeader header={translate('create_journey_component.title_create_new_journey')} className={classes['header-title']} />
                  <p>{translate('create_journey_component.msg_create_new_journey')}</p>
                </div>
                {clonedJourneyDialog && (
                <div className={classes['clone-journey-wrapper']}>
                  <div className={classes['clone-journey-wrapper-content']}>
                    <MyIcon name="infoFillIcon" className={classes['info-icon']} /><span className={classes['font-bold']}>{translate('create_journey_component.clone_journey_text')}</span> {translate('create_journey_component.clone_journey_dialog')}
                  </div>
                  <Button
                    variant="tertiary"
                    className={classes['cancel-icon']}
                    onClick={closeClonedJourneyDialog}
                    data-testid="cancel-clone-popup"
                    aria-label="cancel-clone-popup"
                  >
                    <MyIcon name="xMarkSmallIcon" />
                  </Button>
                </div>
              )}
                <div className={classes['form-data']}>

                  <div className={classes['journey-name-input']}>
                    <Input
                      onChange={nameChangeHandler}
                      value={journeyName}
                      label={undefined}
                      labelText={String(translate('create_journey_component.lbl_journey_name'))}
                      isMandatory
                      helperText={String(translate('create_journey_component.input_helper_text'))}
                      type="text"
                      error={invalidJourneyName !== JourneyValidation.VALID || isJourneyNameInvalid || apiResponseStatus === DataLoadingState.FAILED}
                      data-testid="journeyName"
                      name="journeyName"
                      placeholder={String(translate('create_journey_component.input_placeholder'))}
                      errorTxt={errorText()}
                      isLoading={apiResponseStatus === DataLoadingState.LOADING}
                      infoIconTxt={String(translate('create_journey_component.input_info_bubble'))}
                    />
                  </div>
                  <div className={classes['group-name-dropdown-div']}>
                    <Dropdown
                      label=""
                      labelText={String(translate('create_journey_component.lbl_group_name'))}
                      placeholder={String(translate('create_journey_component.txt_group_name_placeholder'))}
                      data={groupOptions}
                      className={classes['group-name']}
                      value={selectGroup}
                      onChange={selectGroupHandler}
                      data-testid="journeyGroupName"
                    />
                  </div>
                  <div className={classes['description-div']}>
                    <Textarea
                      label=""
                      labelText={String(translate('create_journey_component.lbl_desc'))}
                      className={classes['journey-des']}
                      data-testid="journeyDescription"
                      value={journeyDescription}
                      onChange={descriptionChangeHandler}
                      name="journeyDescription"
                      placeholder={String(translate('create_journey_component.lbl_journey_description'))}
                      infoIconTxt={String(translate('create_journey_component.description_info_bubble'))}
                    />
                  </div>
                  <div className={classes['submit-button']}>
                    <Button
                      onClick={handleCancelCreateJourney}
                      className={classes['cancel-button']}
                      variant="secondary"
                      type="button"
                      data-testid="btn-cancel-create-journey"
                    >
                      {translate('service_wrapper_component.lbl_cancel')}
                    </Button>
                    <Button
                      onClick={submitHandler}
                      className={classes['next-button']}
                      variant="primary"
                      type="button"
                      disabled={invalidJourneyName !== JourneyValidation.VALID || journeyName.trim() === '' || apiResponseStatus !== DataLoadingState.COMPLETED || isJourneyNameInvalid}
                      data-testid="journeyButton"
                    >
                      {translate('create_journey_component.btn_next')}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className={classes['journey-image']} />
      </div>
    </>
  );
};

export default CreateJourney;
